/**
 * Semantic UI icon-name → MUI icon component mapping.
 *
 * Semantic UI icons follow Font Awesome 4 names (`name="user"`, `name="trash alternate"`).
 * MUI icons are PascalCased components (`<PersonIcon/>`, `<DeleteOutlineIcon/>`).
 *
 * This map covers the names actually used in the REMUS codebase. Anything missing falls
 * back to a generic `<HelpOutlineIcon/>` and logs a warning so it's easy to find.
 */
import * as React from 'react';
import ExcelIcon from './ExcelIcon';
import IBeamIcon from './IBeamIcon';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import KeyboardDoubleArrowLeft from '@mui/icons-material/KeyboardDoubleArrowLeft';
import KeyboardDoubleArrowRight from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowUp from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDown from '@mui/icons-material/KeyboardDoubleArrowDown';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowBack from '@mui/icons-material/ArrowBack';
import ArrowForward from '@mui/icons-material/ArrowForward';
import InfoIcon from '@mui/icons-material/Info';
import InfoOutlineIcon from '@mui/icons-material/InfoOutlined';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import UploadIcon from '@mui/icons-material/Upload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import GroupIcon from '@mui/icons-material/Group';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import FilterListIcon from '@mui/icons-material/FilterList';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import StopIcon from '@mui/icons-material/Stop';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailIcon from '@mui/icons-material/Email';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsOffIcon from '@mui/icons-material/NotificationsOff';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListIcon from '@mui/icons-material/List';
import ViewListIcon from '@mui/icons-material/ViewList';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import GridOnIcon from '@mui/icons-material/GridOn';
import BarChartIcon from '@mui/icons-material/BarChart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CloudIcon from '@mui/icons-material/Cloud';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MapIcon from '@mui/icons-material/Map';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SyncIcon from '@mui/icons-material/Sync';
import HistoryIcon from '@mui/icons-material/History';
import PrintIcon from '@mui/icons-material/Print';
import ShareIcon from '@mui/icons-material/Share';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import StorageIcon from '@mui/icons-material/Storage';
import TimelineIcon from '@mui/icons-material/Timeline';
import PieChartIcon from '@mui/icons-material/PieChart';
import BuildIcon from '@mui/icons-material/Build';
import GoogleIcon from '@mui/icons-material/Google';
import WebIcon from '@mui/icons-material/Web';
import LinkIcon2 from '@mui/icons-material/Launch';
import MapsHomeIcon from '@mui/icons-material/Apps';
import DescriptionIcon from '@mui/icons-material/Description';
import ListAltIcon from '@mui/icons-material/ListAlt';
import LayersIcon from '@mui/icons-material/Layers';
import ScienceIcon from '@mui/icons-material/Science';
import EmojiNatureIcon from '@mui/icons-material/EmojiNature';
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp';
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import BlockIcon from '@mui/icons-material/Block';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import EventIcon from '@mui/icons-material/Event';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ContentCopyIcon2 from '@mui/icons-material/ContentCopy';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridViewIcon from '@mui/icons-material/GridView';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import InboxIcon from '@mui/icons-material/Inbox';
import MemoryIcon from '@mui/icons-material/Memory';
import PhoneIcon from '@mui/icons-material/Phone';
import ImageIcon from '@mui/icons-material/Image';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import QrCodeIcon from '@mui/icons-material/QrCode';
import TuneIcon from '@mui/icons-material/Tune';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import AssignmentIcon from '@mui/icons-material/Assignment';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import SearchIcon2 from '@mui/icons-material/ZoomIn';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import HubIcon from '@mui/icons-material/Hub';
import MediationIcon from '@mui/icons-material/Mediation';
import SettingsInputAntennaIcon from '@mui/icons-material/SettingsInputAntenna';
import RouterIcon from '@mui/icons-material/Router';
import FormatAlignJustifyIcon from '@mui/icons-material/FormatAlignJustify';
import SunnySnowingIcon from '@mui/icons-material/SunnySnowing';
import InsightsIcon from '@mui/icons-material/Insights';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WidgetsIcon from '@mui/icons-material/Widgets';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import OpacityIcon from '@mui/icons-material/Opacity';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ForwardIcon from '@mui/icons-material/Forward';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import CodeIcon from '@mui/icons-material/Code';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import LabelIcon from '@mui/icons-material/Label';
import VisibilityOffIcon2 from '@mui/icons-material/VisibilityOff';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import TableChartIcon from '@mui/icons-material/TableChart';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LogoutIcon from '@mui/icons-material/Logout';

const ICONS: Record<string, React.ComponentType<{ fontSize?: 'inherit' | 'small' | 'medium' | 'large' }>> = {
  // Add/remove
  plus: AddIcon, add: AddIcon,
  minus: RemoveIcon, subtract: RemoveIcon,
  // Close
  close: CloseIcon, times: CloseIcon, x: CloseIcon, cancel: CloseIcon, remove: CloseIcon,
  // Check
  check: CheckIcon, checkmark: CheckIcon,
  'check circle': CheckCircleIcon,
  // Edit
  pencil: EditIcon, edit: EditIcon,
  // Delete
  trash: DeleteIcon,
  'trash alternate': DeleteOutlineIcon,
  // Save
  save: SaveIcon,
  // Settings
  cog: SettingsIcon, setting: SettingsIcon, settings: SettingsIcon,
  // Search
  search: SearchIcon,
  // Arrows / chevrons
  'chevron up': KeyboardArrowUp, 'chevron down': KeyboardArrowDown,
  'chevron left': KeyboardArrowLeft, 'chevron right': KeyboardArrowRight,
  'angle up': KeyboardArrowUp, 'angle down': KeyboardArrowDown,
  'angle left': KeyboardArrowLeft, 'angle right': KeyboardArrowRight,
  'angle double up': KeyboardDoubleArrowUp, 'angle double down': KeyboardDoubleArrowDown,
  'angle double left': KeyboardDoubleArrowLeft, 'angle double right': KeyboardDoubleArrowRight,
  'arrow up': ArrowUpward, 'arrow down': ArrowDownward,
  'arrow left': ArrowBack, 'arrow right': ArrowForward,
  'caret up': KeyboardArrowUp, 'caret down': KeyboardArrowDown,
  'caret left': KeyboardArrowLeft, 'caret right': KeyboardArrowRight,
  // Info / warn / error
  info: InfoIcon,
  'info circle': InfoIcon,
  question: HelpOutlineIcon,
  'question circle': HelpOutlineIcon,
  help: HelpOutlineIcon,
  'help circle': HelpOutlineIcon,
  warning: WarningIcon, 'warning sign': WarningIcon, 'warning circle': WarningIcon,
  exclamation: WarningIcon, 'exclamation triangle': WarningIcon,
  error: ErrorIcon,
  // I/O
  download: DownloadIcon,
  upload: UploadIcon,
  copy: ContentCopyIcon,
  'copy outline': ContentCopyIcon,
  cut: ContentCutIcon,
  paste: ContentPasteIcon,
  undo: UndoIcon,
  redo: RedoIcon,
  print: PrintIcon,
  share: ShareIcon,
  'share alternate': ShareIcon,
  'external alternate': OpenInNewIcon,
  external: OpenInNewIcon,
  refresh: RefreshIcon, sync: SyncIcon,
  // Navigation
  home: HomeIcon,
  bars: MenuIcon, sidebar: MenuIcon,
  'ellipsis vertical': MoreVertIcon, 'ellipsis horizontal': MoreHorizIcon,
  // User
  user: PersonIcon, 'user circle': PersonIcon,
  users: GroupIcon,
  // Lock
  lock: LockIcon, 'lock open': LockOpenIcon, unlock: LockOpenIcon,
  // Visibility
  eye: VisibilityIcon, 'eye slash': VisibilityOffIcon, hide: VisibilityOffIcon,
  // Star / favorite
  star: StarIcon, 'star outline': StarBorderIcon, favorite: StarIcon,
  // Filter
  filter: FilterListIcon,
  // Play/pause
  play: PlayArrowIcon, pause: PauseIcon, stop: StopIcon,
  // Time / calendar
  calendar: CalendarTodayIcon,
  'calendar outline': CalendarTodayIcon, time: AccessTimeIcon, clock: AccessTimeIcon,
  history: HistoryIcon,
  // Email / notifications
  mail: EmailIcon, envelope: EmailIcon,
  bell: NotificationsIcon, 'bell slash': NotificationsOffIcon,
  alarm: NotificationsIcon,
  // Layout
  dashboard: DashboardIcon,
  list: ListIcon, 'list alternate': ListIcon, 'list ul': ListIcon, 'list ol': ListIcon,
  'th list': ViewListIcon, th: ViewModuleIcon, grid: GridOnIcon,
  // Charts
  'chart bar': BarChartIcon, 'chart line': ShowChartIcon,
  // Files / folders
  folder: FolderIcon, 'folder open': FolderOpenIcon, file: InsertDriveFileIcon,
  'file alternate': InsertDriveFileIcon, 'file outline': InsertDriveFileIcon,
  // Cloud
  cloud: CloudIcon, 'cloud upload': CloudUploadIcon, 'cloud download': CloudDownloadIcon,
  // Location
  'map marker': LocationOnIcon, 'map marker alternate': LocationOnIcon,
  marker: LocationOnIcon, map: MapIcon, 'map outline': MapIcon,
  'location arrow': LocationOnIcon,
  // Link
  linkify: LinkIcon, link: LinkIcon, unlink: LinkOffIcon, broken_chain: LinkOffIcon,
  // Power
  'power off': PowerSettingsNewIcon, power: PowerSettingsNewIcon, 'sign out': LogoutIcon,
  // Zoom / fullscreen
  'zoom in': ZoomInIcon, 'zoom out': ZoomOutIcon,
  expand: FullscreenIcon, compress: FullscreenExitIcon,
  // Expand/collapse
  'caret square down': ExpandMoreIcon, 'caret square up': ExpandLessIcon,
  // Drag
  'arrows alternate': OpenWithIcon, move: OpenWithIcon, drag: DragIndicatorIcon,
  // Domain-specific (from REMUS sidebar/navbar)
  database: StorageIcon, server: StorageIcon,
  hub: HubIcon, 'device hub': HubIcon,
  mediation: MediationIcon,
  'broadcast tower': SettingsInputAntennaIcon, antenna: SettingsInputAntennaIcon,
  station: SettingsInputAntennaIcon, beacon: SettingsInputAntennaIcon,
  router: RouterIcon,
  'align justify': FormatAlignJustifyIcon,
  'sunny snowing': SunnySnowingIcon, weather: SunnySnowingIcon, meteo: SunnySnowingIcon,
  insights: InsightsIcon,
  candlestick: CandlestickChartIcon, 'candlestick chart': CandlestickChartIcon,
  'warning amber': WarningAmberIcon,
  widgets: WidgetsIcon, wallpaper: WallpaperIcon,
  'chart area': TimelineIcon, 'chart pie': PieChartIcon, 'pie graph': PieChartIcon, 'pie chart': PieChartIcon,
  cogs: BuildIcon,
  google: GoogleIcon,
  'window maximize': WebIcon,
  tm: LinkIcon2, h: LinkIcon2,
  sitemap: MapsHomeIcon,
  'file alternate outline': DescriptionIcon,
  'list alternate outline': ListAltIcon,
  layers: LayersIcon,
  flask: ScienceIcon,
  bug: EmojiNatureIcon,
  cube: LayersIcon, cubes: LayersIcon,
  // App editor toolbar
  'arrow alternate circle up': ArrowCircleUpIcon,
  'arrow alternate circle down': ArrowCircleDownIcon,
  'arrow alternate circle left': ArrowCircleLeftIcon,
  'arrow alternate circle right': ArrowCircleRightIcon,
  'object group': GroupWorkIcon,
  delete: DeleteIcon,
  // Misc additions
  ban: BlockIcon, dont: BlockIcon,
  box: Inventory2Icon,
  'calendar alternate': EventIcon, event: EventIcon,
  camera: PhotoCameraIcon, photo: ImageIcon,
  'clock outline': AccessTimeIcon,
  clone: ContentCopyIcon2,
  columns: ViewColumnIcon,
  dropdown: ArrowDropDownIcon,
  'file excel': ExcelIcon,
  'file pdf': PictureAsPdfIcon,
  'frown outline': SentimentDissatisfiedIcon,
  inbox: InboxIcon,
  microchip: MemoryIcon,
  phone: PhoneIcon,
  point: RadioButtonCheckedIcon,
  qrcode: QrCodeIcon,
  'question circle outline': HelpOutlineIcon,
  'save outline': SaveIcon,
  'search plus': SearchIcon2,
  sliders: TuneIcon,
  'step backward': SkipPreviousIcon,
  tasks: AssignmentIcon,
  'th large': GridViewIcon,
  'window maximize outline': OpenInBrowserIcon,
  wrench: BuildOutlinedIcon,
  'zoom-out': ZoomOutIcon,
  heartbeat: MonitorHeartIcon,
  rain: WaterDropIcon,
  tint: OpacityIcon,
  'line graph': ShowChartIcon,
  attention: WarningIcon,
  'window close': CloseIcon,
  'mail forward': ForwardIcon,
  'checked calendar': EventAvailableIcon,
  // Recently-found gaps (was falling back to HelpOutline + warning):
  image: ImageIcon,
  'arrows alternate horizontal': SwapHorizIcon,
  'arrows horizontal': SwapHorizIcon,
  child: ChildCareIcon,
  code: CodeIcon,
  crosshairs: GpsFixedIcon,
  target: GpsFixedIcon,
  'i cursor': IBeamIcon,
  labeled: LabelIcon,
  'low vision': VisibilityOffIcon2,
  'plus circle': AddCircleIcon,
  'add circle': AddCircleIcon,
  'minus circle': RemoveCircleIcon,
  'remove circle': RemoveCircleIcon,
  'left chevron': KeyboardArrowLeft,
  'right chevron': KeyboardArrowRight,
  'sign-out': LogoutIcon,
  logout: LogoutIcon,
  'step forward': SkipNextIcon,
  table: TableChartIcon,
  'undo alternate': UndoIcon,
  'pencil alternate': EditOutlinedIcon,
  'times circle': HighlightOffIcon,
  'trash alternate outline': DeleteOutlineIcon,
  'trash forever': DeleteForeverIcon,
  'user plus': PersonAddIcon,
};

export function resolveIcon(
  name?: string,
): React.ComponentType<{ fontSize?: 'inherit' | 'small' | 'medium' | 'large' }> {
  if (!name) {
    return HelpOutlineIcon;
  }
  const key = name.trim().toLowerCase();
  const found = ICONS[key];
  if (found) {
    return found;
  }
  // Try the first word — many Semantic icons combine modifier + base ('linkify outline').
  const first = key.split(/\s+/)[0];
  const fallback = ICONS[first];
  if (fallback) {
    return fallback;
  }
  // eslint-disable-next-line no-console
  console.warn(`[ui-compat] No MUI icon mapped for Semantic name "${name}" — falling back to HelpOutline`);
  return HelpOutlineIcon;
}
